import { getConfigTenant, isCaponeTenant, isCorpTenant } from "@capone/common";
import { Box, Typography } from "@material-ui/core";
import clsx from "clsx";
import {
  FunnelNewTag,
  FunnelNewTagType,
  HeaderHomePageMiddleSection,
  HeaderMenuButtonType,
  Icon,
  IconName,
  VacationRentalNewTag,
} from "halifax";
import { IHeaderHomePageMiddleSectionButtonProps } from "halifax/build/Header/components/HeaderHomePageMiddleSection/component";
import React from "react";
import { RouteComponentProps } from "react-router-dom";
import { RewardsAccount } from "redmond";
import { config } from "../../../api/config";
import {
  AVAILABLE,
  CARS_ONLY,
  CONTROL,
  EXPERIENCES_EXPERIMENT,
  FLIGHTS_ONLY,
  getExperimentVariant,
  getExperimentVariantCustomVariants,
  HOTELS_ONLY,
  LC_GATING_EXPERIMENT,
  LUXURY_VACATION_RENTAL_FUNNEL_LAUNCH,
  NOT_SELF_FUNDED,
  PACKAGES_EXPERIMENT,
  PACKAGES_EXPERIMENT_VARIANTS,
  PREMIER_COLLECTION_EXPERIMENT,
  PREMIUM_STAYS_ONLY,
  RESTRICT_FUNNELS,
  RESTRICT_FUNNELS_VARIANTS,
  RestrictFunnelsVariantType,
  SELF_FUNDED,
  STAYS_HOMEPAGE,
  STAYS_VACATION_RENTALS_AWARENESS_MODAL,
  VR_FOR_NON_PREMIUM_CARDHOLDERS_EXPERIMENT,
  useExperiments,
} from "../../../context/experiments";
import {
  PATH_CARS,
  PATH_EXPERIENCES,
  PATH_FLIGHTS,
  PATH_HOTELS,
  PATH_PACKAGES,
  PATH_PREMIER_COLLECTION,
  PATH_STAYS,
} from "../../../utils/urlPaths";

interface FunnelEntryTabsProps extends RouteComponentProps {
  rewardsAccounts?: RewardsAccount[];
  isMobile?: boolean;
}

export const FunnelEntryTabs = (props: FunnelEntryTabsProps) => {
  const { history, isMobile } = props;

  if (isMobile) {
    return null;
  }

  const expState = useExperiments();

  const isCorporate = isCorpTenant(getConfigTenant());

  const vacationFunnelLaunchEnabled =
    getExperimentVariant(
      expState.experiments,
      LUXURY_VACATION_RENTAL_FUNNEL_LAUNCH
    ) === AVAILABLE;

  const isPremierCollectionEnabled =
    getExperimentVariant(
      expState.experiments,
      PREMIER_COLLECTION_EXPERIMENT
    ) === AVAILABLE;

  const isLifestyleEnabled =
    getExperimentVariant(expState.experiments, LC_GATING_EXPERIMENT) ===
    AVAILABLE;

  const isLifestyleOnlyEnabled =
    isLifestyleEnabled && !isPremierCollectionEnabled;

  const isVRForNonPremiumCardHoldersEnabled =
    getExperimentVariant(
      expState.experiments,
      VR_FOR_NON_PREMIUM_CARDHOLDERS_EXPERIMENT
    ) === AVAILABLE;

  const restrictFunnelVariant = getExperimentVariantCustomVariants(
    expState.experiments,
    RESTRICT_FUNNELS,
    RESTRICT_FUNNELS_VARIANTS
  );

  const packagesExperimentVariant = getExperimentVariantCustomVariants(
    expState.experiments,
    PACKAGES_EXPERIMENT,
    PACKAGES_EXPERIMENT_VARIANTS
  );

  const staysHomepageEnabled =
    getExperimentVariant(expState.experiments, STAYS_HOMEPAGE) === AVAILABLE;

  const staysVacationRentalsAwarenessModalEnabled =
    getExperimentVariant(
      expState.experiments,
      STAYS_VACATION_RENTALS_AWARENESS_MODAL
    ) === AVAILABLE;
  const staysVRAwarenessModalLocalStorageKey = "vr_awareness_modal_seen";
  const staysVRAwarenessModalSeen =
    localStorage.getItem(staysVRAwarenessModalLocalStorageKey) != null;

  const isPackagesExperiment = [SELF_FUNDED, NOT_SELF_FUNDED].includes(
    packagesExperimentVariant
  );

  const isExperiencesExperiment =
    getExperimentVariant(expState.experiments, EXPERIENCES_EXPERIMENT) ===
    AVAILABLE;

  const hotelsButton = {
    type: staysHomepageEnabled
      ? HeaderMenuButtonType.Stays
      : HeaderMenuButtonType.Hotels,
    selected:
      history.location.pathname === PATH_HOTELS ||
      history.location.pathname === PATH_STAYS,
    content: (
      <Box className="funnel-button-content">
        <Icon name={IconName.HotelFunnelIcon} />
        <Typography>{staysHomepageEnabled ? "Stays" : "Hotels"}</Typography>
        {staysVacationRentalsAwarenessModalEnabled &&
          !staysVRAwarenessModalSeen && (
            <FunnelNewTag
              enabled
              type={FunnelNewTagType.STAYS_FUNNEL_SEARCHES}
            />
          )}
      </Box>
    ),
    onClick: () =>
      history.push(staysHomepageEnabled ? PATH_STAYS : PATH_HOTELS),
  };

  const flightsButton = {
    type: HeaderMenuButtonType.Flights,
    selected: history.location.pathname === PATH_FLIGHTS,
    content: (
      <Box className="funnel-button-content">
        <Icon name={IconName.FlightFunnelIcon} />

        <Typography>Flights</Typography>
      </Box>
    ),
    onClick: () => history.push(PATH_FLIGHTS),
  };

  const carsButton = {
    type: HeaderMenuButtonType.Cars,
    selected: history.location.pathname === PATH_CARS,
    content: (
      <Box className="funnel-button-content">
        <Icon name={IconName.CarFunnelIcon} />
        <Typography>Rental Cars</Typography>
      </Box>
    ),
    onClick: () => history.push(PATH_CARS),
  };

  const showPremiumFunnel = isLifestyleEnabled && isCaponeTenant(config.TENANT);

  const getPremiumStaysButton =
    (): IHeaderHomePageMiddleSectionButtonProps[] => {
      if (isPremierCollectionEnabled || isVRForNonPremiumCardHoldersEnabled) {
        return [
          {
            type: HeaderMenuButtonType.PremierCollection,
            selected: history.location.pathname.includes(
              PATH_PREMIER_COLLECTION
            ),
            content: (
              <Box className="funnel-button-content premium-stays">
                <Icon name={IconName.StarIconWithLines} />
                <Typography>Premium Stays</Typography>
                <VacationRentalNewTag enabled={vacationFunnelLaunchEnabled} />
              </Box>
            ),
            onClick: () => history.push(PATH_PREMIER_COLLECTION),
          },
        ];
      }

      if (isPremierCollectionEnabled) {
        return [
          {
            type: HeaderMenuButtonType.PremierCollection,
            selected: history.location.pathname.includes(
              PATH_PREMIER_COLLECTION
            ),
            content: (
              <Box className="funnel-button-content premier-collection">
                <Icon name={IconName.PremierCollectionVerticalIcon} />
              </Box>
            ),
            onClick: () => history.push(PATH_PREMIER_COLLECTION),
          },
        ];
      }

      if (isLifestyleOnlyEnabled && !isVRForNonPremiumCardHoldersEnabled) {
        return [
          {
            type: HeaderMenuButtonType.PremierCollection,
            selected: history.location.pathname.includes(
              PATH_PREMIER_COLLECTION
            ),
            content: (
              <Box className="funnel-button-content lifestyle-collection">
                <Icon name={IconName.LifestyleCollectionIcon} />
              </Box>
            ),
            onClick: () => history.push(PATH_PREMIER_COLLECTION),
          },
        ];
      }

      return [];
    };

  const packagesButton = isPackagesExperiment
    ? [
        {
          type: HeaderMenuButtonType.Packages,
          selected: history.location.pathname === PATH_PACKAGES,
          content: (
            <Box className="funnel-button-content">
              <div className="packages-icon-wrapper">
                <Icon name={IconName.HotelFunnelIcon} />
                <Typography className="icon-plus-separator">+</Typography>
                <Icon name={IconName.FlightFunnelIcon} />
              </div>
              <Typography>{isCorporate ? "Trips" : "Packages"}</Typography>
              <FunnelNewTag
                enabled
                type={FunnelNewTagType.PACKAGES_FUNNEL_SEARCHES}
                className={isCorporate ? "corp-new-funnel-tag" : undefined}
              />
            </Box>
          ),
          onClick: () => history.push(PATH_PACKAGES),
        },
      ]
    : [];

  const experiencesButton = isExperiencesExperiment
    ? [
        {
          type: HeaderMenuButtonType.Experiences,
          selected: history.location.pathname === PATH_EXPERIENCES,
          content: (
            <Box className="funnel-button-content">
              <div className="experiences-icon-wrapper">
                <Icon name={IconName.ExperiencesIcon} />
              </div>
              <Typography>Activities</Typography>
              <FunnelNewTag
                enabled
                type={FunnelNewTagType.EXPERIENCES_FUNNEL_SEARCHES}
              />
            </Box>
          ),
          onClick: () => history.push(PATH_EXPERIENCES),
        },
      ]
    : [];

  const buttons = [
    flightsButton,
    hotelsButton,
    ...packagesButton,
    carsButton,
    ...experiencesButton,
    ...(showPremiumFunnel ? getPremiumStaysButton() : []),
  ];

  const getButtons = (variant: RestrictFunnelsVariantType) => {
    switch (variant) {
      case CARS_ONLY:
        return [carsButton];
      case FLIGHTS_ONLY:
        return [flightsButton];
      case HOTELS_ONLY:
        return [hotelsButton];
      case PREMIUM_STAYS_ONLY:
        return getPremiumStaysButton();
      case CONTROL:
      default:
        return buttons;
    }
  };

  return (
    <HeaderHomePageMiddleSection
      className={clsx("b2b", {
        mobile: isMobile,
      })}
      buttons={getButtons(restrictFunnelVariant)}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    />
  );
};
